<template>
	<div class="cia">
		<p class="cia-hint">
			Sirve para el doble conteo puro, como los ingresos por dividendos del holding frente
			al resultado de la filial. La cuenta desaparece del reporte de la consolidada sin línea
			de ajuste; el reporte de cada miembro no cambia.
		</p>

		<ul class="cia-list">
			<li v-for="member in members" :key="member.company_id" class="cia-row">
				<div class="cia-row__who">
					<span class="cia-name">{{ member.name }}</span>
					<span class="cia-rut">{{ member.rut }}</span>
				</div>

				<div class="cia-row__body">
					<template v-if="editing !== member.company_id">
						<span v-if="linesOf(member).length === 0" class="cia-muted">Ninguna</span>
						<span v-else class="cia-chips">
							<span v-for="line in linesOf(member)" :key="line.id" class="cia-chip">
								{{ line.level_3 }}
							</span>
						</span>
					</template>

					<div v-else class="cia-edit">
						<MultiSelectDropdown
							v-model="selected"
							:options="optionsOf(member)"
							placeholder="Elegir cuentas"
							searchable
							search-placeholder="Buscar cuenta…"
						/>
						<p v-if="balanceWarning" class="cia-warning" role="status">
							{{ balanceWarning }}
						</p>
						<div class="cia-actions">
							<button type="button" class="theme_btn_dark" :disabled="saving" @click="save(member)">
								Guardar
							</button>
							<button type="button" class="theme_btn_white" :disabled="saving" @click="cancel">
								Cancelar
							</button>
						</div>
					</div>
				</div>

				<div class="cia-row__action">
					<button
						v-if="editing !== member.company_id"
						type="button"
						class="theme_btn_white"
						:disabled="saving || editing !== null"
						@click="edit(member)"
					>
						Editar
					</button>
				</div>
			</li>
		</ul>
	</div>
</template>

<script>
// Bloque «Cuentas ignoradas» de la pestaña «Consolidación» (plan
// consolidadas, D2). Presentacional: recibe los miembros, las líneas
// ignoradas por miembro y las opciones por miembro; emite `load-options`
// al abrir la edición y `save` con los ids elegidos. El padre habla con el
// store y notifica.
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown";

const BALANCE_LEVELS = ["Activos", "Pasivos", "Patrimonio"];

export default {
	name: "ConsolidatedIgnoredAccounts",
	components: {
		MultiSelectDropdown,
	},
	props: {
		members: {
			type: Array,
			default: () => [],
		},
		// { [member_company_id]: [{ id, account_id, level_3, ... }] }
		ignoredByMember: {
			type: Object,
			default: () => ({}),
		},
		// { [member_company_id]: [{ id, label, level_1 }] }
		optionsByMember: {
			type: Object,
			default: () => ({}),
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			editing: null,
			selected: [],
		};
	},
	computed: {
		// D2: ignorar una cuenta de balance desbalancea A = P + Pat.
		balanceWarning() {
			if (this.editing === null) return "";
			const chosen = new Set(this.selected);
			const balance = this.optionsOf({ company_id: this.editing })
				.filter((option) => chosen.has(option.id) && BALANCE_LEVELS.includes(option.level_1))
				.map((option) => option.label);
			if (!balance.length) return "";
			return `Al ignorar una cuenta de balance (${balance.join(", ")}) la fila `
				+ "Activos = Pasivos + Patrimonio deja de cuadrar en la consolidada. "
				+ "Si la cuenta tiene contrapartida en otro miembro, usa un neteo.";
		},
	},
	methods: {
		linesOf(member) {
			return this.ignoredByMember[member.company_id] || [];
		},
		optionsOf(member) {
			return this.optionsByMember[member.company_id] || [];
		},
		edit(member) {
			this.editing = member.company_id;
			this.selected = this.linesOf(member).map((line) => line.account_id);
			this.$emit("load-options", member.company_id);
		},
		cancel() {
			this.editing = null;
			this.selected = [];
		},
		save(member) {
			this.$emit("save", { memberId: member.company_id, accountIds: [...this.selected] });
		},
		// El padre cierra la edición cuando el guardado termina bien.
		close() {
			this.cancel();
		},
	},
};
</script>

<style lang="scss" scoped>
@use "./account_plan_buttons" as *;

.cia {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding-top: 12px;
}

.cia-hint {
	margin: 0;
	max-width: 70ch;
	font-size: 13px;
	line-height: 1.5;
	color: var(--ap-slate);
}

.cia-list {
	margin: 0;
	padding: 0;
	list-style: none;
}

.cia-row {
	display: grid;
	grid-template-columns: minmax(160px, 1fr) minmax(0, 2fr) auto;
	gap: 16px;
	align-items: start;
	padding: 12px 0;
	border-top: 1px solid var(--ap-line);
}

.cia-name {
	display: block;
	font-weight: 600;
	color: var(--ap-ink);
}

.cia-rut {
	display: block;
	font-size: 12px;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;
}

.cia-chips {
	display: inline-flex;
	flex-wrap: wrap;
	gap: 4px;
}

.cia-chip {
	padding: 2px 8px;
	border-radius: var(--ap-radius-pill);
	background: var(--ap-warn-wash);
	color: var(--ap-warn-ink);
	font-size: 12.5px;
}

.cia-muted {
	color: var(--ap-muted);
	font-size: 13px;
}

.cia-edit {
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-width: 0;
}

.cia-warning {
	margin: 0;
	padding: 10px 12px;
	background: var(--ap-warn-wash);
	border: 1px solid var(--ap-warn);
	border-radius: var(--ap-radius-sm);
	font-size: 12.5px;
	line-height: 1.5;
	color: var(--ap-ink-2);
}

.cia-actions {
	display: flex;
	gap: 8px;
}

@media (max-width: 860px) {
	.cia-row {
		grid-template-columns: 1fr;
		gap: 8px;
	}
}
</style>
